import React from 'react'
import { useAppSelector } from '@app/hooks/reduxHooks'

import { DeviceStatusEnum } from '@/api/device.api'
import { checkIsDeviceWorking, getAllDevicesFromProfile, getDeviceResurseValue, getDeviceStatus } from '@/utils/utils'

import { useDeviceSSE } from './useDeviceSSE'
import { Device } from '@/api/profile.api'

type DeviceStatus = {
  id: string
  isWorking: number
  alertStatus: DeviceStatusEnum
  resource: number
}

export const useDeviceData = ({ selectedValue }) => {
  const [devicesStatuses, setDevicesStatuses] = React.useState<DeviceStatus[]>([])
  const [visibleDevices, setVisibleDevices] = React.useState<Device[]>([])

  const profile = useAppSelector((state) => state.profile?.data)
  const allDevices = getAllDevicesFromProfile(profile)
  const data = useDeviceSSE(allDevices.map(({ serialNumber }) => serialNumber))

  React.useEffect(() => {
    if (!profile) return

    let currentDevices: Device[] = []

    if (selectedValue.devices) {
      currentDevices = selectedValue.devices
    } else if (selectedValue.id === 'additional') {
      currentDevices = profile?.devices
    } else {
      const firmDevices = profile?.firms.flatMap((f) => f.devices || []) || []
      currentDevices = [...firmDevices, ...profile.devices]
    }

    setVisibleDevices(currentDevices)
  }, [selectedValue, profile])

  React.useEffect(() => {
    if (!profile) return

    const devices = getAllDevicesFromProfile(profile)

    setDevicesStatuses((prevStatuses) =>
      devices.map(({ id, serialNumber, alertStatus, resource }) => {
        const realtimeData = data[serialNumber]
        const prevStatus = prevStatuses.find((status) => status.id === id)

        if (realtimeData && realtimeData.length > 0) {
          const isWorking = checkIsDeviceWorking(realtimeData)
          const computedAlertStatus = getDeviceStatus(realtimeData, alertStatus)
          const computedResource = getDeviceResurseValue(realtimeData, resource)

          return {
            id,
            isWorking,
            alertStatus: computedAlertStatus,
            resource: computedResource,
          }
        }

        if (prevStatus) {
          return { ...prevStatus, isWorking: 0 }
        }

        return {
          id,
          alertStatus,
          isWorking: 0,
          resource,
        }
      }),
    )
  }, [data, profile])

  return {
    visibleDevices,
    devicesStatuses,
    setVisibleDevices,
    setDevicesStatuses,
  }
}
